import type React from 'react';
import { useMemo } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { useSignalR } from 'contexts';
import { useLiveIndicatorStyles } from './LiveIndicator.styles';

interface LiveIndicatorProps {
  /** Current timestamp for calculating time since last communication */
  now: number;
  /** Hours of no messages before showing stale warning */
  staleThresholdHours: number;
}

type HealthStatus = 'connected' | 'stale' | 'disconnected';

/**
 * Displays SignalR connection health status for wall-screen mode.
 * - Green: Connected and received messages recently
 * - Yellow: Connected but no messages for configured threshold (potential issue)
 * - Red: Not connected
 */
export const LiveIndicator: React.FC<LiveIndicatorProps> = ({ now, staleThresholdHours }) => {
  const styles = useLiveIndicatorStyles();
  const { connectionStatus, lastCommunicationTime } = useSignalR();

  const { status, timeSinceLastComm } = useMemo(() => {
    const isConnected = connectionStatus === 'connected';
    const staleThresholdMs = staleThresholdHours * 60 * 60 * 1000;
    
    if (!isConnected) {
      return { status: 'disconnected' as HealthStatus, timeSinceLastComm: null };
    }

    if (!lastCommunicationTime) {
      // Connected but never received a message - show as stale
      return { status: 'stale' as HealthStatus, timeSinceLastComm: null };
    }

    const elapsed = now - lastCommunicationTime.getTime();
    
    if (elapsed > staleThresholdMs) {
      return { status: 'stale' as HealthStatus, timeSinceLastComm: elapsed };
    }

    return { status: 'connected' as HealthStatus, timeSinceLastComm: elapsed };
  }, [connectionStatus, lastCommunicationTime, now, staleThresholdHours]);

  const formatTimeSince = (ms: number | null): string => {
    if (ms === null) return 'NEVER';
    
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}S AGO`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}M AGO`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}H AGO`;
    
    const days = Math.floor(hours / 24);
    return `${days}D AGO`;
  };

  const pulseClass = mergeClasses(
    styles.pulse,
    status === 'connected' && styles.pulseGreen,
    status === 'stale' && styles.pulseYellow,
    status === 'disconnected' && styles.pulseRed,
  );

  const statusTextClass = mergeClasses(
    styles.statusText,
    status === 'connected' && styles.statusTextGreen,
    status === 'stale' && styles.statusTextYellow,
    status === 'disconnected' && styles.statusTextRed,
  );

  const statusLabel = status === 'connected' ? 'LIVE' : status === 'stale' ? 'STALE' : 'OFFLINE';

  return (
    <div className={styles.container}>
      <div className={styles.indicator}>
        <div className={pulseClass} />
        <span className={statusTextClass}>{statusLabel}</span>
      </div>
      <span className={styles.lastSeen}>
        {status === 'disconnected' ? 'NO CONNECTION' : `LAST: ${formatTimeSince(timeSinceLastComm)}`}
      </span>
    </div>
  );
};
